// A1 — Autentifikatsiya va kirish. Module-local data layer for the two in-panel
// management screens (A1.02 Hardware 2FA, A1.08 Onboarding). Does NOT touch
// `i18n/*` (HOT) — RU labels live inline in `AUTH_STR` and get lifted to keys
// later (see REGISTER.md). Backend has TOTP (`/admin/account/2fa/*`) but NO
// WebAuthn/security-key endpoints yet → security-key fetchers fail-soft to
// empty; mutations POST when the endpoints ship.
import { api } from '@/lib/api'

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/* ── A1.02 — Hardware 2FA (WebAuthn / security keys) — REAL ─────── */

/** A1.02 — a registered WebAuthn credential. Dates are epoch-ms from the core. */
export interface SecurityKeyRow {
  id: number
  /** Human label the admin gave the key ("YubiKey 5C", "Touch ID"). */
  name: string
  /** Authenticator model id (AAGUID) as a UUID, "" when the model is unknown. */
  aaguid: string
  created_at: number
  last_used_at: number | null
}

/** GET /admin/account/security-keys — the current admin's registered keys. */
export async function fetchSecurityKeys(signal?: AbortSignal): Promise<SecurityKeyRow[]> {
  const data = await api.get<{ keys?: SecurityKeyRow[] }>('/admin/account/security-keys', { signal })
  return data?.keys ?? []
}

/**
 * PublicKeyCredentialCreationOptions as the core returns them (go-webauthn):
 * challenge / user.id / excludeCredentials[].id are base64url strings that the
 * browser needs as ArrayBuffers before `navigator.credentials.create()`.
 */
export interface CredentialCreationJSON {
  publicKey: PublicKeyCredentialCreationOptionsJSON
}
interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string
  rp: { id?: string; name: string }
  user: { id: string; name: string; displayName: string }
  pubKeyCredParams: { type: 'public-key'; alg: number }[]
  timeout?: number
  attestation?: AttestationConveyancePreference
  excludeCredentials?: { type: 'public-key'; id: string; transports?: string[] }[]
  authenticatorSelection?: AuthenticatorSelectionCriteria
}

/** POST /admin/account/security-keys/begin — start a real WebAuthn ceremony. */
export function beginSecurityKeyRegistration(): Promise<CredentialCreationJSON> {
  return api.post<CredentialCreationJSON>('/admin/account/security-keys/begin', {})
}

/** The attestation the browser produced, serialized to the WebAuthn JSON shape. */
export interface AttestationJSON {
  id: string
  rawId: string
  type: string
  authenticatorAttachment?: string
  clientExtensionResults: Record<string, unknown>
  response: {
    clientDataJSON: string
    attestationObject: string
    transports?: string[]
  }
}

/** POST /admin/account/security-keys — finish registration (verify + persist). */
export function finishSecurityKeyRegistration(
  name: string,
  credential: AttestationJSON,
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/admin/account/security-keys', { name, credential })
}

/** POST /admin/account/security-keys/:id/revoke — remove one of the admin's keys. */
export function revokeSecurityKey(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/account/security-keys/${id}/revoke`, {})
}

/* base64url ⇄ ArrayBuffer helpers (no @simplewebauthn/browser dep). */

/** base64url string → ArrayBuffer (for challenge / user.id / excluded cred ids). */
export function b64urlToBuf(b64url: string): ArrayBuffer {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : ''
  const bin = atob(b64 + pad)
  const buf = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i)
  return buf.buffer
}

/** ArrayBuffer → base64url string (for rawId / attestationObject / clientDataJSON). */
export function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Runs the full browser registration ceremony: convert the core's options,
 * call `navigator.credentials.create()`, and serialize the attestation back to
 * the WebAuthn JSON shape the core verifies. Throws on cancel/unsupported.
 */
export async function createBrowserCredential(opts: CredentialCreationJSON): Promise<AttestationJSON> {
  const pk = opts.publicKey
  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: b64urlToBuf(pk.challenge),
    rp: pk.rp,
    user: {
      id: b64urlToBuf(pk.user.id),
      name: pk.user.name,
      displayName: pk.user.displayName,
    },
    pubKeyCredParams: pk.pubKeyCredParams,
    timeout: pk.timeout,
    attestation: pk.attestation,
    authenticatorSelection: pk.authenticatorSelection,
    excludeCredentials: (pk.excludeCredentials ?? []).map((c) => ({
      type: c.type,
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  }

  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
  if (!cred) throw new Error('cancelled')
  const resp = cred.response as AuthenticatorAttestationResponse
  const transports =
    typeof resp.getTransports === 'function' ? resp.getTransports() : undefined

  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults() as Record<string, unknown>,
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      attestationObject: bufToB64url(resp.attestationObject),
      ...(transports && transports.length ? { transports } : {}),
    },
  }
}

/* ── A1.08 — Onboarding progress ────────────────────────────────── */

/** A1.08 — completed onboarding step ids (real per-admin state, /admin/account/onboarding). */
export async function fetchOnboardingProgress(signal: AbortSignal): Promise<Set<string>> {
  const rows = await safeList<{ id: string }>('/admin/account/onboarding', 'completed', signal)
  return new Set(rows.map((r) => r.id))
}

/** PUT /admin/account/onboarding — mark a checklist step done / not-done. */
export function setOnboardingStep(step: string, done: boolean): Promise<{ ok: boolean }> {
  return api.put<{ ok: boolean }>('/admin/account/onboarding', { step, done })
}

/* ── A1.03 — Admin access points (real, /admin/account/devices) ───
   Admin auth is JWT/stateless (no session table), so the honest "devices"
   list is the distinct source IPs the current admin has acted from, derived
   from the audit trail: IP, first/last seen, action count, current-IP marker. */
export interface AdminDevice {
  ip: string
  actions: number
  first_seen: string
  last_seen: string
  current: boolean
}
export const fetchAdminDevices = (s: AbortSignal): Promise<AdminDevice[]> =>
  safeList<AdminDevice>('/admin/account/devices', 'devices', s)

/* ── A1.06 — IP/device blocks (real, reuses /admin/ip-blocks A8.16) ── */
export interface IpBlockRow {
  id: number
  kind: string
  value: string
  reason: string
  hits: number
  created_at: string
}
export const fetchIpBlocks = (s: AbortSignal): Promise<IpBlockRow[]> =>
  safeList<IpBlockRow>('/admin/ip-blocks', 'blocks', s)

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi, REGISTER.md). */
export const AUTH_STR = {
  // A1.02 — Hardware 2FA
  hardware2fa: 'Аппаратный 2FA (ключи безопасности)',
  hardware2faDesc: 'Ключи WebAuthn/FIDO2 для входа в админ-панель',
  label: 'Название',
  keyKind: 'Тип',
  aaguid: 'Модель (AAGUID)',
  added: 'Добавлен',
  lastUsed: 'Последнее использование',
  status: 'Статус',
  register: 'Зарегистрировать ключ',
  revoke: 'Отозвать',
  revoked: 'Ключ отозван',
  failed: 'Не удалось выполнить',
  // key-kind labels
  kindSecurityKey: 'Ключ безопасности',
  kindPlatform: 'Платформенный',
  kindCrossPlatform: 'Внешний',
  // status labels
  stActive: 'Активен',
  stRevoked: 'Отозван',
  // A1.02 — planned notice (no WebAuthn provider yet)
  webauthnPlannedTitle: 'WebAuthn-провайдер ещё не подключён',
  webauthnPlannedDesc:
    'Регистрация и вход по аппаратному ключу станут доступны, когда появится эндпоинт /admin/account/security-keys. Сейчас список пуст.',

  // A1.08 — Onboarding
  onboarding: 'Добро пожаловать / онбординг',
  onboardingDesc: 'Первые шаги для нового администратора MyGram',
  welcomeTitle: 'Добро пожаловать в админ-панель MyGram',
  welcomeBody:
    'Пройдите несколько шагов, чтобы защитить свою учётную запись и освоиться в панели. Отмеченные шаги уже выполнены.',
  progressLabel: 'Прогресс',
  stepDone: 'Готово',
  stepTodo: 'Осталось',
  open: 'Открыть',
  // onboarding steps
  stepProfileTitle: 'Заполните профиль',
  stepProfileDesc: 'Проверьте имя, контактную почту и язык интерфейса.',
  step2faTitle: 'Включите двухфакторную аутентификацию',
  step2faDesc: 'Настройте TOTP-код в разделе «Моя учётная запись» для защиты входа.',
  stepKeyTitle: 'Добавьте аппаратный ключ',
  stepKeyDesc: 'Зарегистрируйте ключ безопасности (WebAuthn) как резервный фактор.',
  stepRolesTitle: 'Изучите свои права',
  stepRolesDesc: 'Посмотрите роли и разрешения, доступные вашей учётной записи.',
  stepAuditTitle: 'Ознакомьтесь с журналом аудита',
  stepAuditDesc: 'Все действия администраторов фиксируются — проверьте, где их смотреть.',
  // onboarding — mark step done/undo (uses PUT /admin/account/onboarding)
  markDone: 'Отметить',
  markUndo: 'Снять',

  // A1.03 — admin access points (real, from audit trail)
  loading: 'Загрузка…',
  deviceNone: 'Пока нет точек входа — они появятся после первых действий в панели',
  deviceActions: 'действий',
  deviceCurrent: 'текущая',
  deviceFirstSeen: 'Первый вход',
  deviceLastSeen: 'Последний вход',

  // A1.04 — break-glass / JIT (real grants + request)
  jitActiveGrants: 'Активные привилегированные доступы',
  jitNone: 'Нет активных грантов',
  jitRequest: 'Запросить доступ',
  jitScope: 'Область (scope)',
  jitReason: 'Причина',
  jitRequested: 'Запрос отправлен',
  jitBreakGlassLog: 'Журнал break-glass',

  // A1.06 — IP blocks (real registry)
  ipBlocksTitle: 'Заблокированные адреса/подсети',
  ipBlocksNone: 'Список блокировок пуст',
  ipHits: 'срабатываний',

  // A1.07 — roles catalog (real)
  rolePermissions: 'разрешений',
  roleAll: 'все права',
  roleAssignable: 'назначаемая',
  roleNone: 'Роли не найдены',
} as const
